import React from 'react';

interface GaugeChartProps {
  value: number;
  label: string;
  size?: number;
  strokeWidth?: number;
}

export const GaugeChart: React.FC<GaugeChartProps> = ({ 
  value, 
  label, 
  size = 120, 
  strokeWidth = 8 
}) => {
  // Ensure value is between 0 and 100
  const clampedValue = Math.max(0, Math.min(100, value));
  
  // Calculate angles for the gauge
  const radius = (size - strokeWidth) / 2;
  const centerX = size / 2;
  const centerY = size / 2;
  const startAngle = -135; // Start at -135 degrees
  const endAngle = 135; // End at 135 degrees
  const totalAngle = endAngle - startAngle;
  
  // Calculate the current angle based on value
  const currentAngle = startAngle + (clampedValue / 100) * totalAngle;
  
  // Convert angles to radians
  const startAngleRad = (startAngle * Math.PI) / 180;
  const currentAngleRad = (currentAngle * Math.PI) / 180;
  
  // Calculate arc coordinates
  const startX = centerX + radius * Math.cos(startAngleRad);
  const startY = centerY + radius * Math.sin(startAngleRad);
  const endX = centerX + radius * Math.cos(currentAngleRad);
  const endY = centerY + radius * Math.sin(currentAngleRad);
  
  // Determine if we need to draw a large arc
  const largeArcFlag = Math.abs(currentAngle - startAngle) > 180 ? 1 : 0;
  
  // Create the path for the progress arc
  const arcPath = `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`;
  
  // Determine color based on value
  const getColor = (val: number) => {
    if (val >= 80) return '#10B981'; // Green
    if (val >= 60) return '#F59E0B'; // Yellow
    return '#EF4444'; // Red
  };
  
  const progressColor = getColor(clampedValue);
  
  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background arc */}
        <path
          d={`M ${startX} ${startY} A ${radius} ${radius} 0 1 1 ${centerX + radius * Math.cos((endAngle * Math.PI) / 180)} ${centerY + radius * Math.sin((endAngle * Math.PI) / 180)}`}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        
        {/* Progress arc */}
        <path
          d={arcPath}
          fill="none"
          stroke={progressColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
      </svg>
      
      {/* Value display */}
      <div className="text-center mt-2">
        <div className="text-2xl font-bold" style={{ color: progressColor }}>
          {clampedValue.toFixed(0)}%
        </div>
        <div className="text-sm text-gray-600 mt-1">{label}</div>
      </div>
    </div>
  );
};

interface ResourcePlanningGaugesProps {
  data: Array<{
    team: string;
    billableRateThisWeek: number;
    billableRateNextWeek: number;
    billableRateIn2Weeks: number;
    billableRateIn3Weeks: number;
  }>;
  isLoading: boolean;
}

export const ResourcePlanningGauges: React.FC<ResourcePlanningGaugesProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold mb-4">Resource Planning - Billable Rates</h3>
        <div className="flex flex-col items-center justify-center space-y-2 h-32">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-600"></div>
          <p className="text-xs text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold mb-4">Resource Planning - Billable Rates</h3>
        <div className="flex items-center justify-center h-32">
          <div className="text-gray-500">Ingen data tilgjengelig</div>
        </div>
      </div>
    );
  }


  // Calculate averages for selected teams
  const calculateAverage = (field: 'billableRateThisWeek' | 'billableRateNextWeek' | 'billableRateIn2Weeks' | 'billableRateIn3Weeks') => {
    const values = data.map(team => team[field]).filter(val => typeof val === 'number' && val > 0) as number[];
    return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
  };

  const averageThisWeek = calculateAverage('billableRateThisWeek');
  const averageNextWeek = calculateAverage('billableRateNextWeek');
  const averageIn2Weeks = calculateAverage('billableRateIn2Weeks');
  const averageIn3Weeks = calculateAverage('billableRateIn3Weeks');

  // Calculate trends (change from previous period)
  const trendNextWeek = averageNextWeek - averageThisWeek;
  const trendIn2Weeks = averageIn2Weeks - averageNextWeek;
  const trendIn3Weeks = averageIn3Weeks - averageIn2Weeks;

  // Helper function to get color class based on value
  // Under 50%: Red, 50-66%: Yellow, Over 66%: Green
  const getColorClass = (value: number) => {
    if (value > 66) return 'text-green-600';
    if (value >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Helper function to get gradient color for gauge
  const getGaugeColor = (value: number) => {
    if (value > 66) return '#10B981'; // Green
    if (value >= 50) return '#F59E0B'; // Amber/Yellow
    return '#EF4444'; // Red
  };

  // Trend indicator component
  const TrendIndicator: React.FC<{ trend: number }> = ({ trend }) => {
    if (Math.abs(trend) < 0.5) return null; // No significant change
    const isPositive = trend > 0;
    return (
      <div className={`flex items-center text-xs mt-1 ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? (
          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        )}
        <span>{Math.abs(trend).toFixed(1)}%</span>
      </div>
    );
  };

  // Enhanced gauge card component
  const GaugeCard: React.FC<{
    title: string;
    value: number;
    trend?: number;
    size?: number;
  }> = ({ title, value, trend, size = 140 }) => {
    const clampedValue = Math.max(0, Math.min(100, value));
    const radius = (size - 12) / 2;
    const centerX = size / 2;
    const centerY = size / 2;
    const startAngle = -135;
    const endAngle = 135;
    const totalAngle = endAngle - startAngle;
    const currentAngle = startAngle + (clampedValue / 100) * totalAngle;
    
    const startAngleRad = (startAngle * Math.PI) / 180;
    const currentAngleRad = (currentAngle * Math.PI) / 180;
    
    const startX = centerX + radius * Math.cos(startAngleRad);
    const startY = centerY + radius * Math.sin(startAngleRad);
    const endX = centerX + radius * Math.cos(currentAngleRad);
    const endY = centerY + radius * Math.sin(currentAngleRad);
    
    const largeArcFlag = Math.abs(currentAngle - startAngle) > 180 ? 1 : 0;
    const arcPath = `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`;
    const fullArcPath = `M ${startX} ${startY} A ${radius} ${radius} 0 1 1 ${centerX + radius * Math.cos((endAngle * Math.PI) / 180)} ${centerY + radius * Math.sin((endAngle * Math.PI) / 180)}`;
    
    const progressColor = getGaugeColor(clampedValue);
    const colorClass = getColorClass(clampedValue);

    return (
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 p-6 border border-gray-100">
        <h4 className="text-sm font-semibold text-gray-700 mb-4 text-center">{title}</h4>
        <div className="flex flex-col items-center">
          <div className="relative">
            <svg width={size} height={size} className="transform -rotate-90">
              {/* Background arc */}
              <path
                d={fullArcPath}
                fill="none"
                stroke="#E5E7EB"
                strokeWidth="12"
                strokeLinecap="round"
              />
              {/* Progress arc with gradient */}
              <defs>
                <linearGradient id={`gradient-${title.replace(/\s+/g, '-')}`} x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor={progressColor} stopOpacity="0.8" />
                  <stop offset="100%" stopColor={progressColor} stopOpacity="1" />
                </linearGradient>
              </defs>
              <path
                d={arcPath}
                fill="none"
                stroke={`url(#gradient-${title.replace(/\s+/g, '-')})`}
                strokeWidth="12"
                strokeLinecap="round"
                className="transition-all duration-500"
              />
            </svg>
            {/* Center value */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className={`text-3xl font-bold ${colorClass}`}>
                {Math.round(clampedValue)}%
              </div>
            </div>
          </div>
          {trend !== undefined && <TrendIndicator trend={trend} />}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Resource Planning - Billable Rates</h3>
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span>&lt;50%</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <span>50-66%</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span>&gt;66%</span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <GaugeCard 
          title="This week" 
          value={averageThisWeek}
        />
        <GaugeCard 
          title="Next week" 
          value={averageNextWeek}
          trend={trendNextWeek}
        />
        <GaugeCard 
          title="In 2 weeks" 
          value={averageIn2Weeks}
          trend={trendIn2Weeks}
        />
        <GaugeCard 
          title="In 3 weeks" 
          value={averageIn3Weeks}
          trend={trendIn3Weeks}
        />
      </div>
    </div>
  );
};
